import React, { useState } from 'react';
import { portfolio } from '../data/mock';
import { ExternalLink } from 'lucide-react';

const PortfolioSection = () => {
  const [filter, setFilter] = useState('All');
  const [hoveredId, setHoveredId] = useState(null);
  const categories = ['All', 'Commercial', 'Documentary', 'Brand Content'];

  const filteredPortfolio = filter === 'All' 
    ? portfolio 
    : portfolio.filter(item => item.category === filter);

  return (
    <section id="portfolio" className="relative bg-black py-20 lg:py-32 overflow-hidden">
      {/* Holographic Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 futuristic-grid" />
        <div className="absolute inset-0 bg-gradient-to-b from-red-600/10 via-transparent to-transparent" />
      </div>
      <div className="absolute inset-0 scanlines" />

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mb-12">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 text-3d">
            Featured{' '}
            <span className="holographic animate-gradient">Work</span>
          </h2>
          <div className="relative">
            <p className="text-lg md:text-xl text-white/70 leading-relaxed">
              A showcase of our most impactful projects for leading brands and agencies worldwide.
            </p>
            <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-red-600 to-transparent" />
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-3 mb-12">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              className={`relative group px-6 py-3 rounded-full text-sm font-medium tracking-wide uppercase transition-all duration-300 overflow-hidden ${
                filter === category
                  ? 'bg-gradient-to-r from-red-600 to-red-500 text-white neon-red scale-105'
                  : 'glass-ultra text-white/70 hover:text-white border border-white/10 hover:border-red-600/50'
              }`}
            >
              <span className="relative z-10">{category}</span>
              {filter !== category && (
                <div className="absolute inset-0 bg-gradient-to-r from-red-600/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              )}
            </button>
          ))}
        </div>

        {/* Portfolio Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPortfolio.map((item, index) => (
            <div
              key={item.id}
              className="group relative transform-3d"
              onMouseEnter={() => setHoveredId(item.id)}
              onMouseLeave={() => setHoveredId(null)}
              style={{
                animation: `fadeInUp 0.6s ease-out ${index * 100}ms both`
              }}
            >
              <div className="relative aspect-[4/5] overflow-hidden rounded-3xl glass-card hover-lift transition-all duration-500">
                {/* Image with Parallax */}
                <div className="relative h-full overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  
                  {/* Holographic Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-transparent opacity-90 group-hover:opacity-95 transition-opacity duration-300" />
                  
                  {/* Scan Lines */}
                  <div className="absolute inset-0 scanlines opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>

                {/* Rotating Border on Hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className="absolute inset-0 rounded-3xl border-2 border-red-600 animate-spin-slow" />
                </div>

                {/* Category Badge */}
                <div className="absolute top-6 left-6 z-20">
                  <div className="relative glass-ultra px-4 py-2 rounded-full border border-red-600/50">
                    <span className="text-red-600 text-xs font-bold uppercase tracking-wider holographic">
                      {item.category}
                    </span>
                    <div className="absolute inset-0 bg-red-600/20 blur-xl" />
                  </div>
                </div>

                {/* External Link Icon */}
                <div className="absolute top-6 right-6 z-20 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                  <div className="glass-ultra p-3 rounded-full border border-white/20 neon-white">
                    <ExternalLink size={20} className="text-white" />
                  </div>
                </div>

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 z-20 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                  <h3 className="text-2xl font-bold text-white mb-2 text-3d">
                    {item.title}
                  </h3>
                  <p className="text-white/80 text-sm mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100">
                    {item.description}
                  </p>
                  <div className="flex items-center justify-between text-xs text-white/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-200">
                    <span className="glass-ultra px-3 py-1 rounded-full">{item.client}</span>
                    <span className="glass-ultra px-3 py-1 rounded-full">{item.year}</span>
                  </div>
                </div>

                {/* Holographic Accent Line */}
                <div className="absolute top-0 left-0 w-0 h-1 holographic group-hover:w-full transition-all duration-700" />
                
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-t from-red-600/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Decorative Orbs */}
      <div className="absolute top-1/3 -left-40 w-80 h-80 bg-red-600 rounded-full blur-[120px] opacity-10 animate-pulse-glow" />
      <div className="absolute bottom-1/3 -right-40 w-80 h-80 bg-white rounded-full blur-[120px] opacity-5 animate-pulse-glow" />
    </section>
  );
};

export default PortfolioSection;
